import React from 'react'
import { Button } from '@/components/ui/button'
import { AlertCircle } from 'lucide-react'

interface ErrorBoundaryProps {
  children: React.ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(JSON.stringify({
      timestamp: new Date().toISOString(),
      level: 'error',
      module: 'ErrorBoundary',
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    }))
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-[hsl(var(--background))] text-center px-4">
          <div className="h-16 w-16 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center mb-4">
            <AlertCircle className="h-8 w-8 text-[hsl(var(--muted-foreground))]" />
          </div>
          <h1 className="text-lg font-semibold mb-1">Something went wrong</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4 max-w-sm">
            An unexpected error occurred. Please reload the page to continue.
          </p>
          <Button onClick={this.handleReload}>Reload page</Button>
          {import.meta.env.DEV && this.state.error && (
            <pre className="mt-6 text-left text-xs text-[hsl(var(--destructive))] bg-[hsl(var(--muted))] p-4 rounded-lg max-w-lg overflow-auto max-h-48">
              {this.state.error.message}
              {'\n'}
              {this.state.error.stack}
            </pre>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
